import type { NormalizedError, SafeLogValue } from './types'

export const REDACTED_VALUE = '[REDACTED]'

const MAX_DEPTH = 5
const MAX_ARRAY_ITEMS = 50
const MAX_OBJECT_KEYS = 100
export const MAX_STRING_LENGTH = 4096
export const MAX_ERROR_MESSAGE_LENGTH = 2000
export const MAX_ERROR_STACK_LENGTH = 16_384
const safeErrorDetails = new WeakMap<object, NormalizedError>()

/**
 * A dependency boundary can replace payload-bearing diagnostics without
 * changing the error that application code catches. Fixed bounded fields only;
 * raw driver causes/payloads are deliberately not copied into the override.
 */
export function registerSafeErrorDetails(
  original: unknown,
  details: Error
): void {
  if (
    (typeof original !== 'object' || original === null) &&
    typeof original !== 'function'
  ) {
    return
  }
  let safe: NormalizedError = {
    kind: 'Error',
    message: 'Error details unavailable',
  }
  try {
    // Keep complete retained fields through normalization. Cutting first can
    // retain a credential fragment that no longer matches a configured secret.
    const bounded = new Error(
      typeof details.message === 'string'
        ? details.message
        : 'Error details unavailable'
    )
    bounded.name = typeof details.name === 'string' ? details.name : 'Error'
    bounded.stack =
      typeof details.stack === 'string' ? details.stack : undefined
    const code = (details as Error & { code?: unknown }).code
    if (typeof code === 'string' || typeof code === 'number') {
      Object.assign(bounded, { code })
    }
    safe = normalizeError(bounded)
    safe.kind = truncate(safe.kind, 128)
    if (safe.code) {
      safe.code = truncate(safe.code, 128)
    }
  } catch {
    // A malformed safe diagnostic must not restore the original raw payload.
  }
  safeErrorDetails.set(original, Object.freeze(safe))
}

const SENSITIVE_KEYS = new Set([
  'authorization',
  'cookie',
  'setcookie',
  'password',
  'passwd',
  'secret',
  'token',
  'accesstoken',
  'refreshtoken',
  'servertoken',
  'serveraccesstoken',
  'apikey',
  'key',
  'keyhash',
  'xapikey',
  'email',
  'firstname',
  'lastname',
  'customfields',
  'prompt',
  'body',
  'payload',
  'content',
  'html',
  'jsx',
  'raw',
  'instructions',
  'snippet',
  'preview',
  'title',
  'subject',
  'sendername',
  'ip',
  'ipaddress',
  'phone',
  'phonenumber',
  'twilionumber',
  'filename',
  'command',
  'creditcard',
  'cardnumber',
  'cvc',
  'cvv',
  'iban',
])

/**
 * Env vars whose VALUES must never appear in an outbound string. Literal
 * values are scrubbed before the shape-based passes because a secret may be
 * shorter than the generic token threshold. Convex `ArgumentValidationError`
 * echoes whole argument objects (which carry `serverAccessToken` on the
 * Resend webhook path); this is the last line before a third-party index.
 */
const SECRET_ENV_KEYS = [
  'INTERNAL_API_SECRET',
  'DATADOG_API_KEY',
  'DD_API_KEY',
  'DD_APP_KEY',
  'API_UPSTASH_REDIS_REST_TOKEN',
  'KV_REST_API_TOKEN',
  'RESEND_API_KEY',
  'CONVEX_DEPLOY_KEY',
  'CLERK_SECRET_KEY',
  'STRIPE_SECRET_KEY',
  'MAILGUN_API_KEY',
  'NOTRA_GEO_TOKEN',
] as const

/** The public-API log payload keys blanked by `redactForLogs`. */
const LEGACY_SENSITIVE_KEY_NAMES = new Set([
  'authorization',
  'xbrewplatformadminsession',
  'xapikey',
  'apikey',
  'key',
  'keyhash',
  'email',
  'firstname',
  'lastname',
  'customfields',
])

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[a-zA-Z]{2,24}\b/g
const TOKEN_PATTERN =
  /\b(?=[A-Za-z0-9_-]{32,}\b)(?=[A-Za-z0-9_-]*[A-Za-z])(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]+\b/g
const SENSITIVE_ASSIGNMENT_PATTERN =
  /\b(authorization|password|passwd|secret|(?:[a-z][\w-]*)?token|api[_-]?key|cookie)\s*[:=]\s*([^\s,;]+)/gi
const ABSOLUTE_URL_PATTERN = /https?:\/\/[^\s"'<>]+/gi
const EMAIL_SEGMENT_PATTERN = /^[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[a-zA-Z]{2,24}$/
const TOKEN_SEGMENT_PATTERN =
  /^(?=[A-Za-z0-9_-]{32,}$)(?=[A-Za-z0-9_-]*[A-Za-z])(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]+$/
/** One URL-safe run of at most 64 characters: the shape of a correlation id. */
const CANONICAL_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/
const SENSITIVE_ROUTE_PARENTS = new Set([
  'contacts',
  'share',
  'webhook',
  'hooks',
  'tokens',
  'credentials',
])

function normalizeKey(key: string): string {
  return key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
}

export function isSensitiveLogKey(key: string): boolean {
  return isSensitiveNormalizedKey(normalizeKey(key))
}

function isSensitiveNormalizedKey(normalized: string): boolean {
  return (
    SENSITIVE_KEYS.has(normalized) ||
    normalized.endsWith('token') ||
    normalized.endsWith('secret') ||
    normalized.endsWith('password') ||
    normalized.endsWith('apikey') ||
    normalized.endsWith('body') ||
    normalized.endsWith('email') ||
    normalized.endsWith('phone') ||
    normalized.endsWith('phonenumber') ||
    normalized.endsWith('prompt') ||
    normalized.endsWith('instructions') ||
    normalized.endsWith('snippet') ||
    normalized.endsWith('preview')
  )
}

export const MAX_MESSAGE_LENGTH = MAX_ERROR_MESSAGE_LENGTH

/**
 * Cap `value` at `maxLength` with an explicit marker, so a cut is never
 * mistaken for the end of the message; the marker counts toward the budget.
 */
export function truncate(value: string, maxLength: number): string {
  return value.length <= maxLength
    ? value
    : `${value.slice(0, Math.max(0, maxLength - 12))}…[truncated]`
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function runtimeEnv(): Record<string, string | undefined> {
  return typeof process === 'undefined' ? {} : process.env
}

const SECRET_PATTERN_CACHE_MAX = 32
const secretPatterns = new Map<string, RegExp>()

/** One compiled pattern per secret value; `replace` resets `lastIndex`. */
function secretPattern(secret: string): RegExp {
  const cached = secretPatterns.get(secret)
  if (cached) {
    return cached
  }
  if (secretPatterns.size >= SECRET_PATTERN_CACHE_MAX) {
    secretPatterns.clear()
  }
  const pattern = new RegExp(escapeRegExp(secret), 'g')
  secretPatterns.set(secret, pattern)
  return pattern
}

/**
 * Replace every occurrence of a configured secret's literal value with
 * `[redacted:<KEY>]`. Values shorter than 8 characters are skipped so a
 * blank or trivial setting cannot redact everything.
 */
export function redactSecretEnvValues(
  value: string,
  env: Record<string, string | undefined> = runtimeEnv()
): string {
  let output = value
  for (const key of SECRET_ENV_KEYS) {
    const secret = env[key]?.trim()
    if (secret && secret.length >= 8) {
      output = output.replace(secretPattern(secret), `[redacted:${key}]`)
    }
  }
  return output
}

type LiteralRedactor = (value: string) => string

function redactNonUrlSensitiveString(
  value: string,
  redactLiteral: LiteralRedactor = redactSecretEnvValues
): string {
  return redactLiteral(value)
    .replace(SENSITIVE_ASSIGNMENT_PATTERN, '$1=[REDACTED]')
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(TOKEN_PATTERN, '[redacted-token]')
}

/**
 * Free-form string pass for `error.message` / `error.stack`: literal secret
 * values first (they may be shorter than the token rule), then emails, then
 * token-shaped runs. `env` is injectable for tests.
 */
export function redactSensitive(
  value: string,
  env: Record<string, string | undefined> = runtimeEnv()
): string {
  return redactSecretEnvValues(value, env)
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(TOKEN_PATTERN, '[redacted-token]')
}

function shouldRedactLegacyKey(key: string): boolean {
  return LEGACY_SENSITIVE_KEY_NAMES.has(normalizeKey(key))
}

function redactLegacyValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((entry) => redactLegacyValue(entry))
  }
  if (!value || typeof value !== 'object') {
    return value
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) =>
      shouldRedactLegacyKey(key)
        ? [key, REDACTED_VALUE]
        : [key, redactLegacyValue(entryValue)]
    )
  )
}

/**
 * Structured pass for the public-API log payloads: blank the values under
 * the credential / contact-PII key names, recursively, keeping the payload's
 * shape (`T` in, `T` out). The canonical serializer applies the wider
 * `sanitizeLogValue`; this one exists for the `lib/api/observability`
 * contract its tests pin.
 */
export function redactForLogs<T>({ value }: { value: T }): T {
  return redactLegacyValue(value) as T
}

export function redactSensitiveString(value: string): string {
  return redactSensitiveStringWith(value, redactSecretEnvValues)
}

function redactSensitiveStringWith(
  value: string,
  redactLiteral: LiteralRedactor
): string {
  return redactNonUrlSensitiveString(value, redactLiteral).replace(
    ABSOLUTE_URL_PATTERN,
    (url) => sanitizeUrlWith(url, redactLiteral)
  )
}

function isUrlNormalizedKey(normalized: string): boolean {
  return (
    normalized === 'url' ||
    normalized === 'uri' ||
    normalized === 'href' ||
    normalized.endsWith('url') ||
    normalized.endsWith('uri')
  )
}

function stringifyUnknown(value: unknown): string {
  try {
    return String(value)
  } catch {
    try {
      return JSON.stringify(value) ?? '[unserializable]'
    } catch {
      return '[unserializable]'
    }
  }
}

function readErrorField(error: object, key: PropertyKey): unknown {
  try {
    return Reflect.get(error, key)
  } catch {
    // Error getters and SDK proxies are diagnostic inputs, never authority.
    return undefined
  }
}

function errorCode(error: object): string | undefined {
  const code = readErrorField(error, 'code')
  return typeof code === 'string' || typeof code === 'number'
    ? truncate(redactSensitiveString(String(code)), MAX_STRING_LENGTH)
    : undefined
}

export function normalizeError(
  error: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): NormalizedError {
  if (
    (typeof error === 'object' && error !== null) ||
    typeof error === 'function'
  ) {
    const safe = safeErrorDetails.get(error)
    if (safe) {
      return safe
    }
  }
  if (depth >= 3) {
    return { kind: 'Error', message: '[Cause depth exceeded]' }
  }

  let isErrorInstance = false
  try {
    isErrorInstance = error instanceof Error
  } catch {
    // A revoked proxy can throw from instanceof too.
  }
  // Workflow replays can deliver SDK errors from the host realm. Their
  // prototype differs, so recognize the fixed Error contract as well.
  const candidate = error && typeof error === 'object' ? error : undefined
  const name = candidate ? readErrorField(candidate, 'name') : undefined
  const message = candidate ? readErrorField(candidate, 'message') : undefined
  if (
    !candidate ||
    (!isErrorInstance &&
      !(typeof name === 'string' && typeof message === 'string'))
  ) {
    return {
      kind: typeof error,
      message: truncate(
        redactSensitiveString(stringifyUnknown(error)),
        MAX_ERROR_MESSAGE_LENGTH
      ),
    }
  }

  const kind = truncate(
    redactSensitiveString(typeof name === 'string' && name ? name : 'Error'),
    MAX_STRING_LENGTH
  )
  if (seen.has(candidate)) {
    return { kind, message: '[Circular error cause]' }
  }
  seen.add(candidate)

  const causes: Array<NormalizedError> = []
  const members = readErrorField(candidate, 'errors')
  const cause = readErrorField(candidate, 'cause')
  let hasMembers = false
  try {
    hasMembers = Array.isArray(members)
  } catch {
    // A malformed aggregate cannot erase its available diagnostic fields.
  }
  if (hasMembers) {
    const entries = members as Array<unknown>
    const count = readErrorField(entries, 'length')
    const limit = typeof count === 'number' ? Math.min(count, 10) : 0
    for (let index = 0; index < limit; index++) {
      causes.push(
        normalizeError(readErrorField(entries, index), depth + 1, seen)
      )
    }
  } else if (cause !== undefined) {
    causes.push(normalizeError(cause, depth + 1, seen))
  }

  const code = errorCode(candidate)
  const stack = readErrorField(candidate, 'stack')
  return {
    kind,
    message: truncate(
      redactSensitiveString(
        typeof message === 'string' && message ? message : kind
      ),
      MAX_ERROR_MESSAGE_LENGTH
    ),
    ...(typeof stack === 'string' && stack
      ? {
          stack: truncate(redactSensitiveString(stack), MAX_ERROR_STACK_LENGTH),
        }
      : {}),
    ...(code ? { code } : {}),
    ...(causes.length > 0 ? { causes } : {}),
  }
}

/** Rebuild an Error after a normalized error crosses a durable boundary. */
export function rehydrateError(normalized: NormalizedError): Error {
  const cause = normalized.causes?.[0]
    ? rehydrateError(normalized.causes[0])
    : undefined
  const error = new Error(normalized.message, cause ? { cause } : undefined)
  error.name = normalized.kind
  if (normalized.stack) {
    error.stack = normalized.stack
  }
  if (normalized.code) {
    Object.assign(error, { code: normalized.code })
  }
  return error
}

/** Keep the originating stack when a durable boundary changes error class. */
export function preserveErrorCause<TError extends Error>(
  error: TError,
  cause: unknown
): TError & { cause: unknown } {
  Object.assign(error, { cause })
  return error as TError & { cause: unknown }
}

/**
 * Where a structured value holds correlation ids, as a tree rooted at that
 * value: `true` marks an id key, an object descends one level. Only these
 * exact paths are ids; the same key anywhere else is an ordinary field.
 */
export type CanonicalIdPaths = {
  readonly [key: string]: CanonicalIdPaths | true
}

type ValueRedactors = {
  string: (value: string) => string
  literal: LiteralRedactor
  url: (value: string) => string
}
const LIVE_REDACTORS: ValueRedactors = {
  string: redactSensitiveString,
  literal: redactSecretEnvValues,
  url: sanitizeUrl,
}

function sanitizeValue(
  value: unknown,
  depth: number,
  ancestors: WeakSet<object>,
  idPaths?: CanonicalIdPaths,
  redactors = LIVE_REDACTORS
): SafeLogValue {
  if (value === null || value === undefined) {
    return value === undefined ? '[Undefined]' : null
  }
  if (typeof value === 'string') {
    return truncate(redactors.string(value), MAX_STRING_LENGTH)
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : String(value)
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'bigint') {
    return value.toString()
  }
  if (typeof value === 'symbol' || typeof value === 'function') {
    return `[${typeof value}]`
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? '[Invalid Date]'
      : value.toISOString()
  }
  if (value instanceof URL) {
    return redactors.url(value.toString())
  }
  if (value instanceof Error) {
    return normalizeError(value) as SafeLogValue
  }
  if (depth >= MAX_DEPTH) {
    return '[MaxDepth]'
  }
  if (ancestors.has(value)) {
    return '[Circular]'
  }

  ancestors.add(value)
  try {
    if (Array.isArray(value)) {
      const items = value
        .slice(0, MAX_ARRAY_ITEMS)
        .map((entry) =>
          sanitizeValue(entry, depth + 1, ancestors, undefined, redactors)
        )
      if (value.length > MAX_ARRAY_ITEMS) {
        items.push(`[${value.length - MAX_ARRAY_ITEMS} more items]`)
      }
      return items
    }

    const output: Record<string, SafeLogValue> = {}
    let inspectedKeys = 0
    for (const key in value) {
      if (!Object.hasOwn(value, key)) {
        continue
      }
      if (inspectedKeys >= MAX_OBJECT_KEYS) {
        output._truncated_keys = '[Additional keys omitted]'
        break
      }
      inspectedKeys += 1
      const normalizedKey = normalizeKey(key)
      if (isSensitiveNormalizedKey(normalizedKey)) {
        output[key] = REDACTED_VALUE
        continue
      }
      try {
        // Read only retained, non-sensitive values. Object.entries would
        // evaluate every getter before applying the output key limit.
        const entryValue = (value as Record<string, unknown>)[key]
        const idPath =
          idPaths && Object.hasOwn(idPaths, key) ? idPaths[key] : undefined
        if (
          isUrlNormalizedKey(normalizedKey) &&
          typeof entryValue === 'string'
        ) {
          output[key] = redactors.url(entryValue)
        } else if (
          idPath === true &&
          typeof entryValue === 'string' &&
          CANONICAL_ID_PATTERN.test(entryValue)
        ) {
          // Canonical IDs skip token-shaped redaction, but never literal
          // configured-secret redaction.
          output[key] = redactors.literal(entryValue)
        } else {
          output[key] = sanitizeValue(
            entryValue,
            depth + 1,
            ancestors,
            idPath === true ? undefined : idPath,
            redactors
          )
        }
      } catch {
        output[key] = '[Unavailable]'
      }
    }
    return output
  } finally {
    ancestors.delete(value)
  }
}

/**
 * Recursive structured pass: key-name redaction, URL scrubbing, then the
 * free-form string pass on every remaining string. A string at one of
 * `idPaths` that has the id shape skips the token rule, so the id stays
 * searchable; any other value there takes the full pass.
 */
export function sanitizeLogValue(
  value: unknown,
  idPaths?: CanonicalIdPaths
): SafeLogValue {
  // Read configured secrets after each retained field getter has resolved.
  // A per-pass environment snapshot can miss a secret rotated by that getter.
  return sanitizeValue(value, 0, new WeakSet(), idPaths)
}

/**
 * Internal serializer only: the caller must prove every retained field is owned
 * primitive data and resolve all external getters first. Never use this for
 * generic logs, frozen caller objects, or across separate emissions.
 */
export function sanitizeOwnedLogValue(
  value: unknown,
  idPaths?: CanonicalIdPaths
): SafeLogValue {
  const literal = createSecretValueRedactor()
  return sanitizeValue(value, 0, new WeakSet(), idPaths, {
    literal,
    string: (text) => redactSensitiveStringWith(text, literal),
    url: (text) => sanitizeUrlWith(text, literal),
  })
}

/**
 * Internal owned-value boundary only: resolve every caller getter first and
 * discard this closure after the synchronous emission. Generic input must
 * retain redactSecretEnvValues' live reads after each field getter.
 */
export function createSecretValueRedactor(): LiteralRedactor {
  const env = runtimeEnv()
  const secrets: Array<{ pattern: RegExp; replacement: string }> = []
  for (const key of SECRET_ENV_KEYS) {
    const secret = env[key]?.trim()
    if (secret && secret.length >= 8) {
      secrets.push({
        pattern: secretPattern(secret),
        replacement: `[redacted:${key}]`,
      })
    }
  }
  return (text) => {
    let output = text
    for (const secret of secrets) {
      output = output.replace(secret.pattern, secret.replacement)
    }
    return output
  }
}

function shouldRedactPathSegment(segment: string, previous: string): boolean {
  if (!segment || /^\[[^\]]+\]$/.test(segment)) {
    return false
  }
  return (
    SENSITIVE_ROUTE_PARENTS.has(previous.toLowerCase()) ||
    EMAIL_SEGMENT_PATTERN.test(segment) ||
    TOKEN_SEGMENT_PATTERN.test(segment)
  )
}

export function sanitizeUrl(value: string): string {
  return sanitizeUrlWith(value, redactSecretEnvValues)
}

function sanitizeUrlWith(
  value: string,
  redactLiteral: LiteralRedactor
): string {
  const isAbsolute = /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(value)
  try {
    const url = new URL(value, 'https://observability.invalid')
    const segments = url.pathname.split('/')
    const sanitizedSegments = segments.map((segment, index) =>
      shouldRedactPathSegment(segment, segments[index - 1] ?? '')
        ? '[redacted]'
        : segment
    )
    const pathname = sanitizedSegments.join('/') || '/'
    let sanitized = pathname
    if (isAbsolute) {
      sanitized = pathname === '/' ? url.origin : `${url.origin}${pathname}`
    }
    // Structured URL fields bypass the ordinary string pass. Query removal
    // alone cannot protect a configured secret retained in the origin/path.
    return redactNonUrlSensitiveString(sanitized, redactLiteral)
  } catch {
    return truncate(
      redactNonUrlSensitiveString(
        value.split(/[?#]/, 1)[0] ?? '',
        redactLiteral
      ),
      512
    )
  }
}
